import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown, Phone, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const langs = ["ar", "en", "ur"];

const Navbar = () => {
  const { lang, setLang, t, dir } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/services/foreign-investment", label: t.nav.foreign },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled ? "sw-glass" : "bg-transparent"}`}
        data-testid="site-navbar"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <BrandMark />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="display text-[15px] md:text-[17px] text-white tracking-tight">{t.brand.name}</span>
              <span className="text-[10px] uppercase tracking-[0.28em] text-white/50 mt-1">Smart Way International</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end
                target={l.newTab ? "_blank" : undefined}
                rel={l.newTab ? "noreferrer" : undefined}
                data-testid={`nav-link-${l.to.replaceAll("/", "-") || "home"}`}
                className={({ isActive }) =>
                  `edit-link text-[13px] uppercase tracking-[0.24em] transition-colors ${isActive ? "text-[color:var(--sw-orange)]" : "text-white/80 hover:text-white"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center rounded-full border border-white/10 p-1 text-[11px] uppercase tracking-widest" data-testid="lang-switcher">
              {langs.map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  data-testid={`lang-btn-${l}`}
                  className={`px-3 py-1 rounded-full transition-colors ${lang === l ? "bg-[color:var(--sw-orange)] text-black" : "text-white/70 hover:text-white"}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <Link
              to="/contact"
              data-testid="cta-request-service"
              className="hidden md:inline-flex items-center gap-2 btn-primary px-5 py-2.5 rounded-full text-[12px] uppercase tracking-[0.18em] font-semibold"
            >
              {t.nav.requestService}
            </Link>

            <button
              onClick={() => setOpen(true)}
              className="lg:hidden p-2 text-white"
              aria-label="Open menu"
              data-testid="mobile-menu-open"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] bg-[color:var(--sw-bg)]"
            data-testid="mobile-menu"
          >
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-3"><BrandMark /><span className="display text-lg text-white">{t.brand.name}</span></div>
              <button onClick={() => setOpen(false)} className="p-2 text-white" aria-label="Close menu" data-testid="mobile-menu-close"><X size={22} /></button>
            </div>
            <div className="px-6 pt-6 space-y-4">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link to={l.to} target={l.newTab ? "_blank" : undefined} rel={l.newTab ? "noreferrer" : undefined} className="display block text-4xl text-white hover:text-[color:var(--sw-orange)] transition-colors">
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <div className="pt-8 flex items-center gap-2 text-[11px] uppercase tracking-widest">
                {langs.map((l) => (
                  <button key={l} onClick={() => setLang(l)} className={`px-3 py-1 rounded-full border ${lang === l ? "bg-[color:var(--sw-orange)] text-black border-[color:var(--sw-orange)]" : "border-white/15 text-white/70"}`}>
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="pt-6 flex gap-3">
                <a href="tel:+966590960314" target="_blank" rel="noreferrer" className="btn-ghost px-4 py-3 rounded-full inline-flex items-center gap-2" data-testid="mobile-call"><Phone size={16}/> 0590960314</a>
                <a href="https://wa.me/966590960314" target="_blank" rel="noreferrer" className="btn-primary px-4 py-3 rounded-full inline-flex items-center gap-2" data-testid="mobile-whatsapp"><MessageCircle size={16}/> WhatsApp</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Brand mark — the actual company logo
export const BrandMark = ({ size = 40 }) => (
  <img
    src="/logo.jpg"
    alt="Smart Way International"
    width={size}
    height={size}
    className="shrink-0 rounded-sm object-contain bg-white p-0.5"
    style={{ width: size, height: size }}
  />
);

export default Navbar;
